import React, { useEffect, useState } from "react";
import {
  Download,
  Loader2,
  ImageIcon,
  Wand2,
  Info,
  X,
  Copy,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";

export interface CanvasVariant {
  assetId: string;
  url: string;
  seed?: number | null;
}

// The generation recipe of the displayed image, for the Info panel.
export interface CanvasDetails {
  toolLabel: string;
  prompt?: string | null;
  negativePrompt?: string | null;
  seed?: number | null;
  params: Record<string, any>;
  createdAt?: string | null;
}

interface CanvasProps {
  imageUrl: string | null;
  seed?: number | null;
  generating: boolean;
  generatingCount?: number;
  onDownload: () => void;
  onUseAsInput: () => void;
  onClear?: () => void;
  // Variant batch from the last generation (strip shows when > 1).
  variants?: CanvasVariant[];
  selectedId?: string | null;
  onSelectVariant?: (assetId: string) => void;
  // Disclosure label on download ("Virtually staged" / "AI-generated").
  showDisclosureToggle?: boolean;
  disclosureOn?: boolean;
  onToggleDisclosure?: (on: boolean) => void;
  // Generation recipe of the displayed image.
  details?: CanvasDetails | null;
  onApplySettings?: () => void;
}

const labelize = (name: string) =>
  name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Canvas: React.FC<CanvasProps> = ({
  imageUrl,
  seed,
  generating,
  generatingCount = 1,
  onDownload,
  onUseAsInput,
  onClear,
  variants = [],
  selectedId,
  onSelectVariant,
  showDisclosureToggle = false,
  disclosureOn = false,
  onToggleDisclosure,
  details,
  onApplySettings,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => {
    setShowInfo(false);
  }, [imageUrl]);

  const copyPrompt = async () => {
    if (!details?.prompt) return;
    try {
      await navigator.clipboard.writeText(details.prompt);
      toast.success("Prompt copied");
    } catch {
      toast.error("Could not copy the prompt");
    }
  };

  const extraParams = Object.entries(details?.params || {}).filter(
    ([k, v]) =>
      !["prompt", "negative_prompt", "seed"].includes(k) &&
      v !== "" &&
      v !== null &&
      v !== undefined
  );

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-6">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="result"
            className="max-h-full max-w-full rounded-lg object-contain shadow-lg"
          />
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-2">
              <ImageIcon size={28} className="text-subtle" />
            </div>
            <p className="text-sm text-muted">Your generated image will appear here</p>
            <p className="max-w-xs text-xs text-subtle">
              Describe what you want, tune the controls, and hit Generate.
            </p>
          </div>
        )}

        {/* Generation details panel */}
        {showInfo && details && imageUrl && (
          <div className="absolute right-4 top-4 z-10 flex max-h-[calc(100%-2rem)] w-72 flex-col rounded-lg border border-border-default bg-surface-overlay shadow-lg">
            <div className="flex items-center justify-between border-b border-border-default px-3 py-2">
              <h3 className="text-xs font-semibold text-primary">Generation details</h3>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="rounded p-0.5 text-subtle hover:text-primary"
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-2.5">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                  Tool
                </div>
                <div className="text-xs text-primary">{details.toolLabel}</div>
              </div>
              {details.prompt && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                      Prompt
                    </span>
                    <button
                      type="button"
                      onClick={copyPrompt}
                      title="Copy prompt"
                      className="rounded p-0.5 text-subtle hover:text-primary"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                  <p className="text-xs leading-snug text-primary">{details.prompt}</p>
                </div>
              )}
              {details.negativePrompt && (
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                    Negative prompt
                  </div>
                  <p className="text-xs leading-snug text-primary">{details.negativePrompt}</p>
                </div>
              )}
              {details.seed != null && (
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                    Seed
                  </div>
                  <div className="text-xs tabular-nums text-primary">{details.seed}</div>
                </div>
              )}
              {extraParams.length > 0 && (
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                    Settings
                  </div>
                  <dl className="mt-0.5 space-y-0.5">
                    {extraParams.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2 text-xs">
                        <dt className="text-muted">{labelize(k)}</dt>
                        <dd className="truncate text-primary">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {details.createdAt && (
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-subtle">
                    Created
                  </div>
                  <div className="text-xs text-primary">
                    {new Date(details.createdAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            {onApplySettings && (
              <div className="border-t border-border-default p-2.5">
                <button
                  type="button"
                  onClick={onApplySettings}
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-xs font-medium text-muted hover:bg-surface-3 hover:text-primary"
                >
                  <SlidersHorizontal size={13} /> Apply these settings
                </button>
              </div>
            )}
          </div>
        )}

        {generating && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={28} className="animate-spin text-violet-500" />
              <span className="text-sm text-muted">
                {generatingCount > 1
                  ? `Generating ${generatingCount} variants…`
                  : "Generating…"}
              </span>
            </div>
          </div>
        )}
      </div>

      {variants.length > 1 && (
        <div className="flex items-center justify-center gap-2 border-t border-border-default px-3 py-2">
          {variants.map((v, i) => (
            <button
              key={v.assetId}
              type="button"
              onClick={() => onSelectVariant?.(v.assetId)}
              title={`Variant ${i + 1}${v.seed != null ? ` — seed ${v.seed}` : ""}`}
              className={`overflow-hidden rounded-md border-2 transition-all ${
                v.assetId === selectedId
                  ? "border-violet-500"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={v.url} alt={`variant ${i + 1}`} className="h-14 w-14 object-cover" />
            </button>
          ))}
        </div>
      )}

      {imageUrl && (
        <div className="flex flex-wrap items-center justify-center gap-2 border-t border-border-default p-3">
          <button
            type="button"
            onClick={onUseAsInput}
            className="flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-muted hover:text-primary hover:bg-surface-3"
          >
            <Wand2 size={15} /> Use as input
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-muted hover:text-primary hover:bg-surface-3"
          >
            <Download size={15} /> Download
          </button>
          {details && (
            <button
              type="button"
              onClick={() => setShowInfo((v) => !v)}
              title="Generation details"
              className={`flex items-center gap-1.5 rounded-md border border-border-default px-3 py-1.5 text-sm ${
                showInfo
                  ? "bg-surface-3 text-primary"
                  : "bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary"
              }`}
            >
              <Info size={15} /> Info
            </button>
          )}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              title="Clear the canvas"
              className="flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-muted hover:bg-surface-3 hover:text-primary"
            >
              <X size={15} /> Clear
            </button>
          )}
          {showDisclosureToggle && (
            <label
              title="Adds a “Virtually staged” / “AI-generated” label to the downloaded file. Most MLSs require virtually staged photos to be labeled."
              className="flex cursor-pointer items-center gap-1.5 text-xs text-muted hover:text-primary"
            >
              <input
                type="checkbox"
                checked={disclosureOn}
                onChange={(e) => onToggleDisclosure?.(e.target.checked)}
                className="h-3.5 w-3.5 accent-violet-500"
              />
              Disclosure label
            </label>
          )}
          {seed != null && (
            <span className="ml-1 text-xs tabular-nums text-subtle">seed {seed}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default Canvas;
